import type { ReactNode } from "react";
import { NewsFeedSwitcher } from "@/components/ui/navigation/NewsFeedSwitcher";
import { UserDropdown } from "@/components/ui/navigation/UserDropdown";
import { appEnvironmentAccent } from "@/config";
import type { NewsFeedItem } from "@/data-access/news-feeds/queries/news-feeds-queries";
import { cn } from "@/lib/utils";
import HeaderLayout from "./HeaderLayout";

export interface PageHeaderProps {
  /** Page label (e.g. "Discovery", "Analysis", "Dynamic Share of Voice"). Shown on sm+; hidden on small screens. Left border only when feed switcher is shown. */
  label?: string;
  /** Optional badge shown in the left area (e.g. "Beta"). When {@link label} is present, badge appears after the label. */
  trailingBadge?: string;
  /** When true, show the feed switcher dropdown (Discovery/Analysis only per design). When false, only label + user dropdown (e.g. Dynamic Share of Voice, News Alerts). */
  showFeedSwitcher?: boolean;
  /** Feeds for the feed switcher. Only used when showFeedSwitcher is true. */
  feeds?: NewsFeedItem[];
  /** Currently selected feed ID (e.g. from route params). */
  selectedFeedId?: string | null;
  /** Called when the user selects a different feed (e.g. navigate to new feed). */
  onFeedChange?: (feedId: string) => void;
  /** When true, show feed image or fallback in switcher. */
  displayFeedImages?: boolean;
  /** User email for {@link UserDropdown}. */
  userEmail: string;
  /** Tenant name for {@link UserDropdown}. */
  userTenantName?: string;
  /** Called when the user selects Log out. */
  onLogout?: () => void;
  /** Optional page navigation shown after the label. */
  navigationSlot?: ReactNode;
  /** Optional content to show in the right area before the user dropdown (e.g. header actions). */
  rightSlot?: ReactNode;
  /** When true (default), stack left/right in a column on small screens. When false, keep row at all breakpoints. */
  stackOnMobile?: boolean;
  /** Applied to the outer header element. */
  className?: string;
}

/**
 * Page header for Discovery/Analysis-style pages: optional label + left border (hidden on small screens),
 * feed switcher dropdown, and user dropdown. Uses {@link HeaderLayout}, {@link NewsFeedSwitcher}, and {@link UserDropdown}.
 */
export const PageHeader = ({
  label,
  trailingBadge,
  showFeedSwitcher = false,
  feeds = [],
  selectedFeedId = null,
  onFeedChange,
  displayFeedImages = true,
  userEmail,
  userTenantName,
  onLogout,
  navigationSlot,
  rightSlot,
  stackOnMobile = false,
  className,
}: PageHeaderProps) => {
  const shouldShowSwitcher = showFeedSwitcher && feeds.length > 0;

  const hasLabelOrBadge = label != null || trailingBadge != null;
  const leftContent = (
    <div className="flex min-w-0 flex-1 items-center gap-4">
      {hasLabelOrBadge && (
        <div
          className={cn(
            "hidden shrink-0 items-center gap-4 sm:flex",
            showFeedSwitcher && "border-r-[1.5px] border-gray-200 pr-4",
          )}
        >
          {label != null && (
            <span className="text-sm font-medium text-violet-950">{label}</span>
          )}
          {trailingBadge != null && (
            <span className="rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-green-900">
              {trailingBadge}
            </span>
          )}
        </div>
      )}
      {appEnvironmentAccent != null && (
        <span
          className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
          style={{
            color: appEnvironmentAccent.badgeColor,
            backgroundColor: `color-mix(in srgb, ${appEnvironmentAccent.badgeColor} 16%, white)`,
          }}
        >
          {appEnvironmentAccent.label}
        </span>
      )}
      {navigationSlot != null && (
        <div className="flex self-stretch">{navigationSlot}</div>
      )}
      {shouldShowSwitcher && (
        <div className="min-w-0 shrink">
          <NewsFeedSwitcher
            feeds={feeds}
            selectedFeedId={selectedFeedId}
            onFeedChange={onFeedChange}
            displayImages={displayFeedImages}
          />
        </div>
      )}
    </div>
  );

  const rightContent = (
    <div className="flex flex-row-reverse items-center gap-2">
      <UserDropdown
        email={userEmail}
        tenantName={userTenantName}
        onLogout={onLogout}
      />
      {rightSlot}
    </div>
  );

  return (
    <header className={cn("sticky top-0 z-10 bg-white", className)}>
      <HeaderLayout
        className={cn("pt-2", navigationSlot ? "pb-0" : "pb-2")}
        stackOnMobile={stackOnMobile}
      >
        {leftContent}
        {rightContent}
      </HeaderLayout>
    </header>
  );
};
